import { Injectable } from '@angular/core';
import {Observable, of, ReplaySubject} from "rxjs";
import {HttpService} from "./http.service";
import {CacheService} from "./cache.service";
import {environment} from "../../environments/environment";
import {map, mapTo, mergeMap, tap} from "rxjs/operators";
import {HttpResponse} from "@angular/common/http";
import {InspectionProperty, InspectionPropertyWriteModel} from "../models/InspectionProperty";

@Injectable({
  providedIn: 'root'
})
export class InspectionPropertyService {

    private _customerInspectionProperties$: ReplaySubject<Array<any>> = new ReplaySubject(1);
    customerInspectionProperties: Array<any>;

    get inspectionProperties$(): Observable<Array<any>> {
        return this._customerInspectionProperties$.asObservable();
    }

    constructor(private httpService: HttpService,
                private cacheService: CacheService
    ) {

        this.inspectionProperties$.subscribe(
            inspectionProperties => {
                this.customerInspectionProperties = inspectionProperties;
            }
        )
    }
    // TODO: Create a type
    getInspectionProperties(skipCache: boolean = false): Observable<Array<InspectionProperty>> {
        if (!skipCache && this.cacheService.hasItem('currentCustomerInspectionProperties', 0)) {
            return of(this.cacheService.getItem('currentCustomerInspectionProperties', 0));
        } else {
            return this.fetchCustomerInspectionProperties();
        }
    }

    private fetchCustomerInspectionProperties(): Observable<Array<InspectionProperty>> {
        return this.httpService.get<Array<InspectionProperty>>(environment.apiCommonV3BaseUrl + '/inspection-properties', {}).pipe(
            map((customerInspectionProperties: Array<InspectionProperty>) =>
                customerInspectionProperties.filter(
                    (property) => {
                        return !(property.name.startsWith('$$') && property.name.endsWith('$$'))
                    }
                )
            ),
            tap((customerInspectionProperties: Array<InspectionProperty>) => this.cacheService.setItem('currentCustomerInspectionProperties', 0, customerInspectionProperties)),
            tap((customerInspectionProperties: Array<InspectionProperty>) => this._customerInspectionProperties$.next(customerInspectionProperties))
        );
    }

    refreshAllInspectionProperties(): Observable<Array<InspectionProperty>> {
        return this.fetchCustomerInspectionProperties().pipe(
            tap((customProperties: Array<InspectionProperty>) => {
                this._customerInspectionProperties$.next(customProperties);
            })
        );
    }

    createInspectionProperty(inspectionProperty: InspectionPropertyWriteModel): Observable<boolean> {
        return this.httpService.post(environment.apiCommonV3BaseUrl + '/inspection-properties', inspectionProperty, { observe: 'response' }).pipe(
            map((res: HttpResponse<any>) => res.ok),
            mergeMap(
                () => this.refreshAllInspectionProperties()
            ),
            mapTo(true)
        );
    }

    saveInspectionProperty(inspectionProperty: InspectionProperty) {
        return this.httpService.patch(environment.apiCommonV3BaseUrl + '/inspection-properties/' + inspectionProperty.id, inspectionProperty,{ observe: 'response' }).pipe(
            tap((res: HttpResponse<any>) => {
                if (!res.ok) throw new Error('Error when saving custom property.');
            }),
            mergeMap(
                () => this.refreshAllInspectionProperties()
            ),
            mapTo(true)
        );
    }

    updateInspectionPropertyStatus(propertyId: number, status: 'ENABLED' | 'DISABLED'): Observable<boolean> {
        return this.httpService.patch(environment.apiCommonV3BaseUrl + '/inspection-properties/' + propertyId, { status },{ observe: 'response' }).pipe(
            tap((res: HttpResponse<any>) => {
                if (!res.ok) throw new Error('Error when disabling custom property.');
            }),
            mergeMap(
                () => this.refreshAllInspectionProperties()
            ),
            mapTo(true)
        );
    }
}
